import {
  FeaturePhotoValueType,
  ParsedFeaturePhotoValue
} from "@/components/map/types";
import { FeatureLinkItem } from "@/components/map/utils";
import { GradientColors } from "@/constants/colors";
import type { Timezone } from "@/constants/dateTime";
import {
  ONLY_NUMERIC_REGEX,
  ONLY_NUMERIC_REGEX_TRIM_PREFIX_ZERO
} from "@/constants/regex";
import { capitalizeFirst } from "@/helpers/string";
import {
  DataSchemaType,
  DatasetSchemaLabel,
  EColorNames
} from "@/types/dataset";

import { formatNumber } from "./number";

/**
 * Formats a value based on its declared data schema type.
 *
 * - `INT` and `FLOAT`: Formatted using `formatNumber`, rounded up to 3 decimal places if needed.
 * - `DATE_TIME`, `DATE`, `TIME`: Formatted using `formatDate` with the corresponding format type.
 * - `LATITUDE` and `LONGITUDE`: Formatted as decimal numbers with 6 decimal places.
 * - `PERCENTAGE`: Formatted as a decimal number with 2 decimal places, suffixed with a `%` sign.
 * - `RATIO`: Formatted as a decimal number with 4 decimal places.
 * - `BOOL`: Capitalized string representation ("True" or "False").
 * - Other types: Returned as-is.
 *
 * @param type - The `DataSchemaType` enum indicating the data type.
 * @param value - The value to be formatted (string or number).
 * @param timezone - (Optional) Timezone for date formatting.
 * @param maximumFractionFloat - (Optional) Maximum decimal places for float formatting (default is 3).
 * @param isRoundUp - (Optional) Whether to round up float values (default is true).
 * @param isFormatDate - (Optional) Whether to format date values (default is true).
 * @param label - (Optional) Dataset schema label for special formatting cases.
 * @param isTruncate - (Optional) Whether to truncate float values instead of rounding (default is false).
 * @returns A formatted string or the original value if no formatting is applicable.
 */
export function formatRenderValue({
  type,
  value,
  maximumFractionFloat = 3,
  isRoundUp = true,
  label,
  isTruncate = false
}: {
  type: DataSchemaType;
  value: string | number | boolean | null | undefined;
  timezone?: Timezone;
  maximumFractionFloat?: number;
  isRoundUp?: boolean;
  isFormatDate?: boolean;
  label?: DatasetSchemaLabel;
  isTruncate?: boolean;
}): string | number {
  if (value === null || value === undefined) {
    return "";
  }

  // Make sure boolean values are converted to string
  const safeBooleanValue = typeof value === "boolean" ? String(value) : value;

  // Make sure it is of type string, not number
  const safeStringValue =
    typeof safeBooleanValue === "string"
      ? safeBooleanValue
      : String(safeBooleanValue);

  switch (type) {
    case DataSchemaType.INT:
      return formatNumber(Number(safeStringValue));
    case DataSchemaType.FLOAT:
      if (
        label === DatasetSchemaLabel.LATITUDE ||
        label === DatasetSchemaLabel.LONGITUDE
      ) {
        return formatNumber(
          Number(safeStringValue),
          "decimal",
          "en-US",
          6,
          6,
          isRoundUp,
          isTruncate
        );
      }
      if (label === DatasetSchemaLabel.PERCENTAGE) {
        return `${formatNumber(Number(safeStringValue), "decimal", "en-US", 2, 2, isRoundUp, isTruncate)}%`;
      }
      if (label === DatasetSchemaLabel.RATIO) {
        return formatNumber(
          Number(safeStringValue),
          "decimal",
          "en-US",
          4,
          4,
          isRoundUp,
          isTruncate
        );
      }
      return formatNumber(
        Number(safeStringValue),
        "decimal",
        "en-US",
        0,
        maximumFractionFloat,
        isRoundUp,
        isTruncate
      );
    case DataSchemaType.BOOL:
      return capitalizeFirst(safeStringValue);
    default:
      return safeBooleanValue;
  }
}

/**
 * Returns a color name from the Gradient Color based on index.
 * The function uses modulo operation to cycle through available color names,
 * ensuring that any index value maps to a valid color name.
 *
 * @param index - The index used to select a color name.
 * @returns A valid key from the COLORS object.
 *
 * @example
 * getPatternColorNameByIndex(0); // "TEAL"
 * getPatternColorNameByIndex(10); // "GRAY" (repeats if COLORS has 10 keys)
 */
export function getPatternColorNameByIndex(index: number): EColorNames {
  return GradientColors[index % GradientColors.length] as EColorNames;
}

/**
 * Splits a string by double colon ("::") and maps the values to an object using a template or numeric keys by default.
 *
 * @param input - The string to split by "::"
 * @param template - (Optional) An object with keys that define the output structure
 * @returns An object with mapped keys to split values
 *
 * @example
 * // With template
 * splitDoubleColon("70294ab3-b8f0-4e17-8115-c0a5241b4edf::31", { uuid: "", code: "" });
 * // Returns: { uuid: "70294ab3-b8f0-4e17-8115-c0a5241b4edf", code: "31" }
 *
 * // Without template
 * splitDoubleColon("a::b::c");
 * // Returns: { "1": "a", "2": "b", "3": "c" }
 */
export const splitDoubleColon = (
  input: string,
  template?: Record<string, string>
): Record<string, string> => {
  const parts = input.split("::").map((s) => s.trim());

  const keys = template
    ? Object.keys(template)
    : parts.map((_, i) => String(i + 1));

  const result: Record<string, string> = {};
  const len = Math.min(keys.length, parts.length);

  for (let i = 0; i < len; i++) {
    result[keys[i]] = parts[i];
  }

  return result;
};

export function trimPhoneNumberByCountryCode(countryCode: string) {
  // Before setting the value, we need to remove any non-numeric characters
  // This is to ensure that the phone number only contains digits
  const regex = ["+39", "+378", "+379"].includes(countryCode)
    ? ONLY_NUMERIC_REGEX
    : ONLY_NUMERIC_REGEX_TRIM_PREFIX_ZERO;

  return regex;
}

const hasLikelyHostname = (hostname: string): boolean => {
  if (!hostname) return false;
  return hostname === "localhost" || hostname.includes(".");
};

const tryCreateHttpUrl = (input: string): string | null => {
  try {
    const url = new URL(input);
    if (
      !["http:", "https:"].includes(url.protocol) ||
      !hasLikelyHostname(url.hostname)
    )
      return null;
    return url.toString();
  } catch {
    return null;
  }
};

const ensureHttpUrl = (value: string): string | null => {
  if (!value) return null;

  return tryCreateHttpUrl(value) ?? tryCreateHttpUrl(`https://${value}`);
};

export const mapToLinkItem = (input: string): FeatureLinkItem | null => {
  if (!input || typeof input !== "string") return null;

  const url = ensureHttpUrl(input.trim());
  return url ? { url } : null;
};

const tryParseJsonValue = (raw: string): unknown => {
  const value = raw.trim();
  const startsWithJsonToken =
    value.startsWith("[") || value.startsWith("{") || value.startsWith('"');

  if (!startsWithJsonToken) return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const mapObjectToLinkItem = (candidate: unknown): FeatureLinkItem | null => {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }

  const { url, label } = candidate as {
    url?: unknown;
    label?: unknown;
  };

  if (typeof url !== "string") return null;

  const normalized = mapToLinkItem(url);
  if (!normalized) return null;

  if (typeof label === "string" && label.trim()) {
    return { ...normalized, label: label.trim() };
  }

  return normalized;
};

const collectLinkItems = (rawValue: unknown): FeatureLinkItem[] => {
  if (typeof rawValue === "string") {
    const single = mapToLinkItem(rawValue.trim());
    return single ? [single] : [];
  }

  if (Array.isArray(rawValue)) {
    return rawValue.flatMap((item) =>
      Array.isArray(item) ? [] : collectLinkItems(item)
    );
  }

  const mapped = mapObjectToLinkItem(rawValue);
  return mapped ? [mapped] : [];
};

export const parseFeatureLinkItems = (value: string): FeatureLinkItem[] => {
  if (!value) return [];

  const trimmedValue = value.trim();
  if (!trimmedValue) return [];

  const parsedValue = tryParseJsonValue(trimmedValue);
  return collectLinkItems(parsedValue);
};

export const getFaviconUrl = (url: string): string | null => {
  try {
    const hostname = new URL(url).hostname;
    return hostname
      ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=48`
      : null;
  } catch {
    return null;
  }
};

export const formatLinkLabel = (linkItem: FeatureLinkItem): string => {
  try {
    const url = new URL(linkItem.url);
    const hostname = url.hostname.replace(/^www\./i, "");
    return hostname || linkItem.url;
  } catch {
    return linkItem.url;
  }
};

export const parseFeaturePhotoValue = (
  rawValue: string
): ParsedFeaturePhotoValue | null => {
  const parsedValue = tryParseJsonValue(rawValue);

  if (Array.isArray(parsedValue)) {
    const items = parsedValue.filter((item) => Boolean(item));

    if (items.length === 0) return null;

    const hasObjectEntry = parsedValue.some(
      (item) =>
        typeof item === "object" && item !== null && !Array.isArray(item)
    );

    return {
      type: hasObjectEntry
        ? FeaturePhotoValueType.ARRAY_OBJECT
        : FeaturePhotoValueType.ARRAY_VALUE,
      items
    };
  }

  return {
    type: FeaturePhotoValueType.SINGLE,
    items: []
  };
};

// Helper function to check if value is a JSON string array
export const tryParseJsonArray = (val: unknown): unknown[] | null => {
  if (typeof val !== "string") return null;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Removes special characters from a string and replaces them with spaces.
 * Consecutive spaces resulting from multiple special characters are collapsed into one.
 *
 * @param str - The input string to process
 * @returns The cleaned string with special characters replaced by single spaces
 *
 * @example
 * removeSpecialCharacters("AREA_KM2")       // "AREA KM2"
 * removeSpecialCharacters("AREA__KM2")      // "AREA KM2"
 * removeSpecialCharacters("AREA@KM#2!")     // "AREA KM 2"
 */
export const removeSpecialCharacters = (str: string): string => {
  return str
    .replaceAll(/[^a-zA-Z0-9\s]/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim();
};
